import { useMemo } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { usePricingData } from './usePricingData';
import type { PlanType, LifetimeCapLimits } from '@/services/pricingService';
import { logger } from '@/services/loggerService';
import { mergeExtraFeatures } from '@/utils/extraFeatures';

export interface PlanFeatures {
  aiTitle: boolean;
  aiSummary: boolean;
  aiChapters: boolean;
  aiContentAnalysis: boolean;
  aiKeyPoints: boolean;
  aiTagging: boolean;
  aiTagManagement: boolean;
  audioRecording: boolean;
  commenting: boolean;
  teamCollaboration: boolean;
  analytics: boolean;
  adminControls: boolean;
  desktopDownload: boolean;
}

export interface CurrentPlanInfo {
  features: PlanFeatures;
  monthlyVideos: number;
  storageDuration: string;
  storageType: 'unlimited' | '30day';
  planName: string;
  planType: PlanType;
  videoDuration: string;
  videoQuality: string;
  video_seconds: number;
  videoQualityNumber: number;
  // Lifetime cap specific fields
  maxActiveVideos: number;
  activeVideosUsed: number;
  // AI processing trial cap (null = unlimited)
  maxAiNotes: number | null;
  aiProcessedCount: number;
  // Team-plan seat cap from pricing/current. null when the user is not on a
  // team plan or the plan has no default_seats configured.
  defaultSeats: number | null;
  betaFeatures: Record<string, boolean>;
  isLoading: boolean;
  error: string | null;
}

/**
 * Hook to get the current user's plan features and limits
 */
export const useCurrentPlanFeatures = (): CurrentPlanInfo => {
  const { userData } = useAuthStore();
  const { pricingData, loading: pricingLoading, error: pricingError } = usePricingData();

  return useMemo(() => {
    if (pricingLoading || !pricingData) {
      logger.debug('subscription', 'Plan features unresolved - pricing data not ready', {
        pricingLoading,
        hasPricingData: Boolean(pricingData),
        pricingError,
        subscriptionProductId: userData?.subscription?.product_id ?? null,
        subscriptionPriceId: userData?.subscription?.price_id ?? null,
      });
      return {
        // Admin-granted extras still apply when pricing is loading or failed to load,
        // matching the API and desktop merges.
        features: mergeExtraFeatures<PlanFeatures>(
          {
            aiTitle: false,
            aiSummary: false,
            aiChapters: false,
            aiContentAnalysis: false,
            aiKeyPoints: false,
            aiTagging: false,
            aiTagManagement: false,
            audioRecording: false,
            commenting: false,
            teamCollaboration: false,
            analytics: false,
            adminControls: false,
            desktopDownload: false,
          },
          userData?.extraFeatures,
        ),
        monthlyVideos: 0,
        storageDuration: '',
        storageType: '30day' as const,
        planName: 'Unknown',
        planType: 'rate_based' as PlanType,
        videoDuration: '5 mins',
        videoQuality: '720p',
        video_seconds: 300,
        videoQualityNumber: 720,
        maxActiveVideos: 0,
        activeVideosUsed: 0,
        maxAiNotes: null,
        defaultSeats: null,
        aiProcessedCount: 0,
        betaFeatures: mergeExtraFeatures({}, userData?.extraBetaFeatures),
        isLoading: pricingLoading,
        error: pricingError,
      };
    }

    // Default to free plan if no subscription
    let currentPlan = pricingData.free;
    let foundMatchingPlan = false;

    // Try to find the plan based on subscription
    if (pricingData && userData?.subscription) {
      const subscription = userData.subscription;

      // First try by price_id (for paid plans)
      if (subscription.price_id) {
        const planByPrice = Object.values(pricingData).find(
          (tier) =>
            tier.prices?.month?.stripePriceId === subscription.price_id ||
            tier.prices?.year?.stripePriceId === subscription.price_id,
        );

        if (planByPrice) {
          currentPlan = planByPrice;
          foundMatchingPlan = true;
        }
      }

      // If no price match, try by product_id
      if (!foundMatchingPlan && subscription.product_id) {
        const planByProduct = Object.values(pricingData).find((tier) => tier.product_id === subscription.product_id);

        if (planByProduct) {
          currentPlan = planByProduct;
          foundMatchingPlan = true;
        }
      }
    }

    // Determine plan type - check subscription first for lifetime plans without pricing data
    const planType: PlanType = userData?.subscription?.plan_type || currentPlan.plan_type || 'rate_based';

    // Check if this is a lifetime cap plan without a matching pricing entry
    const isLifetimePlanWithoutPricingEntry = planType === 'lifetime_cap' && !foundMatchingPlan;

    // Get max active videos for lifetime cap plans
    const maxActiveVideos =
      planType === 'lifetime_cap'
        ? userData?.subscription?.max_active_videos || (currentPlan.limits as LifetimeCapLimits)?.max_active_videos || 0
        : 0;

    // For lifetime plans without pricing entry, use subscription's product_name
    const planName = isLifetimePlanWithoutPricingEntry
      ? userData?.subscription?.product_name || userData?.subscription?.plan || 'Lifetime Plan'
      : currentPlan.name || 'FREE';

    logger.debug('subscription', 'Resolved current plan features', {
      planName,
      planType,
      matchedPlanByPricingData: foundMatchingPlan,
      subscriptionProductId: userData?.subscription?.product_id ?? null,
      subscriptionPriceId: userData?.subscription?.price_id ?? null,
      aiKeyPoints: currentPlan.features?.aiKeyPoints ?? false,
      aiChapters: currentPlan.features?.aiChapters ?? false,
      aiTagging: currentPlan.features?.aiTagging ?? false,
    });

    const planFeatures: PlanFeatures = currentPlan.features || {
      aiTitle: false,
      aiSummary: false,
      aiChapters: false,
      aiContentAnalysis: false,
      aiKeyPoints: false,
      aiTagging: false,
      aiTagManagement: false,
      audioRecording: false,
      commenting: false,
      teamCollaboration: false,
      analytics: false,
      adminControls: false,
      desktopDownload: false,
    };

    return {
      // Plan features plus any the admin granted this user (extra_features).
      features: mergeExtraFeatures(planFeatures, userData?.extraFeatures),
      monthlyVideos: currentPlan.monthlyVideos || 0,
      storageDuration: currentPlan.storageDuration || '30 days',
      storageType: currentPlan.storageType || '30day',
      planName,
      planType,
      videoDuration: currentPlan.videoDuration || '5 mins',
      videoQuality: currentPlan.videoQuality || '720p',
      video_seconds: currentPlan.video_seconds || 300,
      videoQualityNumber: currentPlan.videoQualityNumber || 720,
      maxActiveVideos,
      activeVideosUsed: userData?.subscription?.active_videos_used || 0,
      maxAiNotes:
        typeof (currentPlan as { max_ai_notes?: number | null }).max_ai_notes === 'number'
          ? ((currentPlan as { max_ai_notes?: number | null }).max_ai_notes ?? null)
          : null,
      defaultSeats:
        typeof (currentPlan as { default_seats?: number }).default_seats === 'number'
          ? (currentPlan as { default_seats?: number }).default_seats!
          : null,
      aiProcessedCount: userData?.subscription?.ai_processed_count || 0,
      betaFeatures: mergeExtraFeatures(currentPlan.betaFeatures || {}, userData?.extraBetaFeatures),
      isLoading: false,
      error: null,
    };
  }, [
    userData?.subscription,
    userData?.extraFeatures,
    userData?.extraBetaFeatures,
    pricingData,
    pricingLoading,
    pricingError,
  ]);
};
